import React from 'react';
import PropTypes from 'prop-types';
import { compose, branch, withProps } from 'recompose';
import BootstrapModal from 'react-bootstrap/lib/Modal';
import omit from 'lodash/omit';
import pick from 'lodash/fp/pick';

import LoadingScreen from '../../../LoadingScreen';
import { Endpoints } from '../../../lib/LeanLinkService';
import { withCreate, withPatch } from '../../../entities/Entity/containers';
import Button from '../../Button';
import PersonForm, { withPersonForm } from '../PersonForm';
import withNotifications from '../../../components/Notifications/withNotifications';
import { withCustomer } from '../../../containers';
import withContacts from '../../../containers/withContacts';
import { useTranslation } from 'react-i18next';

function CreateModal({ show, onHide, isLoading, handleSubmit, dirty, ...props }) {
  const { t } = useTranslation();
  return (
    <BootstrapModal
      show={show}
      onHide={() => {
        if (dirty && !confirm(t('Är du säker på att du vill avsluta? Informationen kommer då att försvinna.'))) {
          return;
        }
        onHide();
      }}
    >
      <LoadingScreen isLoading={isLoading} />
      <BootstrapModal.Header closeButton>
        <BootstrapModal.Title>
          {t("Skapa ny kontakt")}
        </BootstrapModal.Title>
      </BootstrapModal.Header>
      <BootstrapModal.Body>
        <PersonForm {...props} />
      </BootstrapModal.Body>
      <BootstrapModal.Footer>
        <Button onClick={handleSubmit} disabled={isLoading}>
          {t("Skapa kontakt")}
        </Button>
      </BootstrapModal.Footer>
    </BootstrapModal>
  );
}

CreateModal.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  show: PropTypes.bool.isRequired,
  dirty: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
};

export default compose(
  withNotifications,
  withPatch(),
  branch(
    ({ customerId }) => !!customerId,
    compose(
      withCustomer,
      withProps(({ customer }) => ({
        contactsIds: customer.contacts ? customer.contacts : [],
      })),
      withContacts,
    ),
  ),
  withCreate({
    props: props => ({
      ...props,
      onSubmit: (values, dispatch, reduxProps) => {
        props.create({
          url: Endpoints.people.create(),
          entity: omit(values.toJS(), 'role'),
        }).then(({ entities: { people } }) => {
          reduxProps.reset();
          const peopleValues = Object.values(people);

          if (peopleValues.length === 0) {
            throw new Error('No people created');
          }

          return peopleValues[0];
        }).then((person) => {
          if (props.newRole) {
            const newContact = {
              role: values.get('role'),
              person: person.id,
              customer: props.customerId,
            };
            const normalizedContacts = props.contacts.map(pick(['role', 'person', 'customer']));
            props.patch({
              url: Endpoints.customers.get({ id: props.customerId }),
              entity: {
                contacts: [
                  ...normalizedContacts,
                  newContact,
                ],
              },
            }).then(() => {
              props.addSuccess({ message: 'Kundkontakterna är uppdaterade' });
            });
          }
          return person;
        }).then(person => props.onSuccess(person));
      },
    }),
  }),
  withPersonForm,
)(CreateModal);
